import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { UserSession } from "@/types/session";
import { cn } from "@/lib/utils";
import { ResetSessionModal } from "@/components/modals/ResetSessionModal";
import { useEffect, useState } from "react";
import { useSession } from "@/hooks/useSession";
import { ExpiredSessionModal } from "@/components/modals/ExpiredSessionModal";

export const SessionInfo = ({
  session,
  isLoading = false,
  disabled = false,
  onResetSession,
}: {
  session: UserSession | null;
  isLoading?: boolean;
  disabled?: boolean;
  onResetSession?: () => void;
}) => {
  const { extendSession, closeSession } = useSession();
  const navigate = useNavigate();

  const [countdown, setCountdown] = useState<string>(formatCountdown(session?.expires_at));
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isExpiredSessionModalOpen, setIsExpiredSessionModalOpen] = useState(false);

  useEffect(() => {
    if (!session?.expires_at) return;

    setCountdown(formatCountdown(session?.expires_at));
    const interval = setInterval(() => {
      const isExpired = new Date(session?.expires_at).getTime() < Date.now();
      if (isExpired) {
        setIsExpiredSessionModalOpen(true);
        clearInterval(interval);
        return;
      }
      setCountdown(formatCountdown(session?.expires_at));
    }, 1000);

    return () => clearInterval(interval);
  }, [session?.expires_at]);

  const showResetSessionModal = () => setIsResetModalOpen(true);
  const handleResetSession = async () => {
    setIsPending(true);
    await closeSession(session);
    onResetSession?.();
    setIsPending(false);
    setIsResetModalOpen(false);
  };

  const handleExtendSession = async () => {
    setIsPending(true);
    await extendSession(session.id);
    setIsPending(false);
    setIsExpiredSessionModalOpen(false);
  };

  const handleCloseSession = async () => {
    setIsPending(true);
    await closeSession(session);
    onResetSession?.();
    setIsPending(false);
    setIsExpiredSessionModalOpen(false);
    navigate("/");
  };

  return (
    <>
      <div
        className={cn(
          "mx-2 flex min-h-0 shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2",
          !session && "invisible"
        )}
      >
        <div className="min-w-0 text-xs text-white/60 sm:text-sm">
          Started on {session && formatDate(session.created_at)}
        </div>

        <button
          disabled={disabled || isLoading}
          onClick={showResetSessionModal}
          className={cn(
            "text-white/60 hover:text-white text-xs sm:text-sm underline",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          Reset Session
        </button>
      </div>

      <ResetSessionModal
        isOpen={isResetModalOpen}
        onOpenChange={(isOpen) => setIsResetModalOpen(isOpen)}
        onConfirm={handleResetSession}
        isLoading={isPending}
        onCancel={() => setIsResetModalOpen(false)}
      />

      <ExpiredSessionModal
        isOpen={isExpiredSessionModalOpen}
        onOpenChange={(isOpen) => setIsExpiredSessionModalOpen(isOpen)}
        onConfirm={handleExtendSession}
        isLoading={isPending}
        onCancel={handleCloseSession}
      />
    </>
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return format(date, "EEEE, d-MMM-yyyy, h:mm a");
  } catch {
    return "N/A";
  }
};

const formatCountdown = (expiresAt?: string) => {
  if (!expiresAt) return "N/A";

  const diff = new Date(expiresAt).getTime() - Date.now();
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
